import React, { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { Alert, Avatar, Empty, Input, Spin, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  EnvironmentOutlined,
  MailOutlined,
  NotificationOutlined,
  PhoneOutlined,
  SearchOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import NotificationModal from "./components/NotificationModal";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import martEmptyImg from "assets/images/mart-empty.png";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  clearVendorsError,
  fetchVendors,
} from "store/slices/vendorsSlice";
import type { AdminVendor } from "types/vendor";

interface ApprovedVendorsPageProps {}

const getInitials = (name: string) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

export const ApprovedVendorsPage: React.FunctionComponent<
  ApprovedVendorsPageProps
> = () => {
  const dispatch = useAppDispatch();
  const { error, loading, vendors } = useAppSelector((state) => state.vendors);
  const [notificationVendor, setNotificationVendor] =
    useState<AdminVendor | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchVendors());
  }, [dispatch]);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return vendors;

    return vendors.filter((vendor) =>
      [
        vendor.vendor_name || "",
        vendor.email || "",
        vendor.phone || "",
        vendor.market_name || "",
        vendor.market_location || "",
        vendor.store_name || "",
      ].some((value) => value.toLowerCase().includes(keyword))
    );
  }, [vendors, search]);

  const columns: ColumnsType<AdminVendor> = [
    {
      title: "Vendor Name",
      dataIndex: "vendor_name",
      key: "vendor_name",
      render: (_, record) => (
        <div className="approved-vendors-cell-vendor">
          <Avatar
            size={40}
            style={{
              background: "#E8F5E9",
              color: "#1a1a1a",
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {getInitials(record.vendor_name || "Vendor")}
          </Avatar>
          <div className="approved-vendors-vendor-info">
            <div className="approved-vendors-vendor-name">
              {record.vendor_name || "—"}
            </div>
            <div className="approved-vendors-vendor-mart">
              {record.market_name || "—"}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email?: string) => (
        <div className="approved-vendors-cell-email">
          <MailOutlined className="approved-vendors-icon-email" />
          <span>{email || "—"}</span>
        </div>
      ),
    },
    {
      title: "Number",
      dataIndex: "phone",
      key: "phone",
      render: (phone?: string) => (
        <div className="approved-vendors-cell-phone">
          <PhoneOutlined className="approved-vendors-icon-phone" />
          <span>{phone || "—"}</span>
        </div>
      ),
    },
    {
      title: "Market",
      key: "market",
      render: (_, record) => (
        <div className="approved-vendors-cell-mart">
          <div
            className="approved-vendors-mart-logo"
            style={{ backgroundColor: "#E3F2FD" }}
          >
            <ShopOutlined />
          </div>
          <div className="approved-vendors-mart-info">
            <div className="approved-vendors-mart-name">
              {record.market_name || "—"}
            </div>
            <div className="approved-vendors-mart-address">
              <EnvironmentOutlined className="approved-vendors-icon-pin" />
              {record.market_location || "—"}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Store",
      key: "store",
      render: (_, record) => (
        <div className="approved-vendors-cell-store">
          <div
            className="approved-vendors-store-logo"
            style={{ backgroundColor: "#E8F5E9" }}
          >
            <ShopOutlined />
          </div>
          <span className="approved-vendors-store-name">
            {record.store_name || "—"}
          </span>
        </div>
      ),
    },
    {
      title: "",
      key: "notification",
      width: 56,
      align: "center",
      render: (_, record) => (
        <button
          type="button"
          className="approved-vendors-notification-btn"
          onClick={() => setNotificationVendor(record)}
          aria-label={`Send notification to ${record.vendor_name}`}
        >
          <NotificationOutlined />
        </button>
      ),
    },
  ];

  return (
    <React.Fragment>
      <CmsTopBar title="Vendor" />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          <div className="approved-vendors-card">
            <div className="approved-vendors-toolbar">
              <div className="approved-vendors-title">
                Vendors{" "}
                <span className="approved-vendors-count">
                  ({filtered.length})
                </span>
              </div>

              <div className="approved-vendors-search-box">
                <Input
                  placeholder="Search"
                  prefix={
                    <SearchOutlined className="approved-vendors-search-icon" />
                  }
                  className="approved-vendors-search-input"
                  allowClear
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </div>

            {error && (
              <Alert
                type="error"
                message={error}
                showIcon
                closable
                className="mb-3"
                onClose={() => dispatch(clearVendorsError())}
              />
            )}

            {loading ? (
              <div className="approved-vendors-empty-wrap">
                <Spin size="large" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="approved-vendors-empty-wrap">
                <Empty
                  image={
                    <div
                      style={{
                        height: 130,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={martEmptyImg}
                        alt="No vendors"
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </div>
                  }
                  description={
                    <div style={{ textAlign: "center", marginTop: 25 }}>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: "#1f1f1f",
                        }}
                      >
                        No vendors found.
                      </div>
                    </div>
                  }
                />
              </div>
            ) : (
              <Table<AdminVendor>
                className="approved-vendors-table"
                columns={columns}
                dataSource={filtered}
                rowKey="vendor_id"
                pagination={{
                  position: ["bottomCenter"],
                  defaultPageSize: 10,
                  showSizeChanger: false,
                  showPrevNextJumpers: true,
                  total: filtered.length,
                  itemRender: (page, type, originalElement) => {
                    if (type === "prev") return <span>Previous</span>;
                    if (type === "next") return <span>Next &gt;</span>;
                    return originalElement;
                  },
                }}
              />
            )}

            <NotificationModal
              open={!!notificationVendor}
              vendorName={notificationVendor?.vendor_name}
              onClose={() => setNotificationVendor(null)}
              onSend={(description) => {
                const name = notificationVendor?.vendor_name || "vendor";
                if (!description) {
                  message.warning("Please enter a description.");
                  return;
                }
                message.success(`Notification sent to ${name}.`);
                setNotificationVendor(null);
              }}
            />
          </div>
        </Container>
      </Container>
    </React.Fragment>
  );
};

export default ApprovedVendorsPage;
